// Mera Naksha — official brand mark.
// Used in every site header and any other UI surface that previously rendered
// the temporary "MN" / HomeIcon monogram. The logo is served as a static
// asset from /brand/mera-naksha-logo.png so we never depend on a remote CDN
// at request time. If the asset ever fails to load (rare — same-origin), we
// fall back to a styled "Mera Naksha" wordmark so headers never collapse.
//
// The current logo artwork has the official tagline baked into the image
// itself, so this component does NOT render a separate text tagline. The
// `withTagline` and `taglineTone` props are accepted for backwards-
// compatibility with existing callers but are intentionally no-ops.
import React, { useState } from "react";
import { Link } from "react-router-dom";

const LOGO_SRC = "/brand/mera-naksha-logo.png";

export const BrandLogo = ({
  size = "md",          // sm (h-9) · md (h-12) · lg (h-16)
  asLink = true,
  to = "/",
  // eslint-disable-next-line no-unused-vars
  withTagline = false,  // retained for backwards compatibility — see header.
  // eslint-disable-next-line no-unused-vars
  taglineTone = "navy",
  // eslint-disable-next-line no-unused-vars
  lang = "en",
  testId = "brand-logo",
  className = "",
}) => {
  const [errored, setErrored] = useState(false);
  // Heights bumped a notch (sm 28 → 36px, md 40 → 48px, lg 48 → 64px) now
  // that the logo carries its own tagline and lock-up — gives the mark
  // more presence and a more premium feel.
  const heightClass =
    size === "sm" ? "h-9" : size === "lg" ? "h-16" : "h-12";

  if (errored) {
    const fallback = (
      <span
        className="inline-flex items-center font-extrabold tracking-tight text-navy text-[16px]"
        data-testid={`${testId}-fallback`}
      >
        Mera Naksha
      </span>
    );
    if (!asLink) return fallback;
    return (
      <Link to={to} data-testid={testId} className="inline-flex items-center shrink-0">
        {fallback}
      </Link>
    );
  }

  const img = (
    <img
      src={LOGO_SRC}
      onError={() => setErrored(true)}
      alt="Mera Naksha — Building Space Today, Building Wealth Tomorrow"
      className={`${heightClass} w-auto select-none ${className}`}
      data-testid={`${testId}-img`}
      draggable={false}
    />
  );

  if (!asLink) return img;
  return (
    <Link to={to} data-testid={testId} className="inline-flex items-center shrink-0">
      {img}
    </Link>
  );
};

export default BrandLogo;
